import {
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
  UseQueryOptions,
  UseQueryResult,
} from 'react-query';
import {
  getChartExpensesByCategory,
  getChartExpensesByYear,
  ChartExpenseByYearResponse,
  updateProfile,
  changeProfilePassword,
  me,
  resetPassword,
  getAllUsers,
  createUser,
  updateUser,
  deleteUser,
  createInstrument,
  deleteInstrument,
  getAllInstruments,
  getInstrument,
  updateInstrument,
  createCertification,
  deleteCertification,
  getAllCertifications,
  getCertification,
  updateCertification,
  getGraphs,
  saveColor,
  getDashboardOverview,
  getAllMeasurements,
  getMeasurement,
  createMeasurement,
  updateMeasurement,
  closeMeasurement,
  deleteMeasurement,
} from 'src/api';

import ChartExpenseByCategory from 'src/models/chart-expense-by-category';
import ApiError from 'src/models/api-error';
import User, { Me } from 'src/models/user';
import Instrument from 'src/models/instrument';
import Measurement from 'src/models/measurement';
import { DashboardYard } from 'src/models/dashboard';
import GraphsResponse from 'src/models/graphs';
import Yard from 'src/models/yard';
import { createYard, deleteYard, getAllYards, getYard, updateYard } from 'src/api/yards-api';
import { createLog, deleteLog, getAllLogs, updateLog } from 'src/api/logs-api';
import Log from 'src/models/log';
import { getTemplateInfo, uploadTemplate, TemplateInfo } from 'src/api/configuration-api';
import { createClient, deleteClient, getAllClients, updateClient } from 'src/api/clients-api';
import Client from 'src/models/client';
import Certification from 'src/models/certification';

// Dashboard api
export const useGetChartExpensesByYear = (
  environmentId: string | number | undefined,
  options?: UseQueryOptions<
    ChartExpenseByYearResponse | undefined,
    unknown,
    ChartExpenseByYearResponse | undefined,
    [string, string | number | undefined]
  >
): UseQueryResult<ChartExpenseByYearResponse | undefined, unknown> =>
  useQuery<
    ChartExpenseByYearResponse | undefined,
    unknown,
    ChartExpenseByYearResponse | undefined,
    [string, string | number | undefined]
  >({
    queryKey: ['chartExpensesByYear', environmentId],
    queryFn: () => {
      if (environmentId === undefined) {
        return Promise.resolve(undefined);
      }
      return getChartExpensesByYear(environmentId);
    },
    initialData: undefined,
    ...options,
  });

export const useGetChartExpensesByCategory = (
  environmentId: string | number | undefined,
  year: string | number | undefined,
  options?: UseQueryOptions<
    ChartExpenseByCategory[] | undefined,
    unknown,
    ChartExpenseByCategory[] | undefined,
    [string, string | number | undefined, string | number | undefined]
  >
): UseQueryResult<ChartExpenseByCategory[] | undefined, unknown> =>
  useQuery<
    ChartExpenseByCategory[] | undefined,
    unknown,
    ChartExpenseByCategory[] | undefined,
    [string, string | number | undefined, string | number | undefined]
  >({
    queryKey: ['chartExpensesByCategory', environmentId, year],
    queryFn: () => {
      if (environmentId === undefined) {
        return Promise.resolve(undefined);
      }
      return getChartExpensesByCategory(environmentId, year);
    },
    initialData: [],
    ...options,
  });

export const useGetDashboardOverview = (
  options?: UseQueryOptions<DashboardYard[], unknown, DashboardYard[], [string]>
): UseQueryResult<DashboardYard[], unknown> =>
  useQuery<DashboardYard[], unknown, DashboardYard[], [string]>({
    queryKey: ['dashboardOverview'],
    queryFn: getDashboardOverview,
    initialData: [],
    ...options,
  });

export const useSaveColor = () => {
  const queryClient = useQueryClient();

  return useMutation((data: { title: string; value: string }) => saveColor(data.title, data.value), {
    onSuccess: () => {
      void queryClient.invalidateQueries(['dashboardOverview']);
    },
  });
};

export const useGetGraphs = (
  measurementId: number | string | undefined,
  title?: string,
  options?: UseQueryOptions<GraphsResponse | undefined, unknown, GraphsResponse | undefined, [string, number | string | undefined, string | undefined]>
): UseQueryResult<GraphsResponse | undefined, unknown> =>
  useQuery<GraphsResponse | undefined, unknown, GraphsResponse | undefined, [string, number | string | undefined, string | undefined]>({
    queryKey: ['graphs', measurementId, title],
    queryFn: () => {
      if (!measurementId) return Promise.resolve(undefined);
      return getGraphs(measurementId, title);
    },
    initialData: undefined,
    ...options,
  });

// instruments
export const useGetInstrument = (id: number | string) =>
  useQuery({ queryKey: ['instruments', id], queryFn: () => getInstrument(id) });
export const useGetAllInstruments = (
  options?: UseQueryOptions<Instrument[], unknown, Instrument[], [string]>
) =>
  useQuery<Instrument[], unknown, Instrument[], [string]>({
    queryKey: ['instruments'],
    queryFn: getAllInstruments,
    initialData: [],
    ...options,
  });
export const useCreateInstrument = (
  options?: UseMutationOptions<Instrument, unknown, Instrument>
) => {
  const queryClient = useQueryClient();

  return useMutation(createInstrument, {
    onSuccess: (newTag) => {
      void queryClient.invalidateQueries(['instruments']);
    },
    ...options,
  });
};
export const useUpdateInstrument = () => {
  const queryClient = useQueryClient();

  return useMutation(updateInstrument, {
    onSuccess: () => {
      void queryClient.invalidateQueries(['instruments']);
    },
  });
};
export const useDeleteInstrument = () => {
  const queryClient = useQueryClient();

  return useMutation((instrument: Instrument) => deleteInstrument(instrument.id), {
    onSuccess: () => {
      void queryClient.invalidateQueries(['instruments']);
    },
  });
};

// measurements
export const useGetMeasurement = (id: number | string) =>
  useQuery({ queryKey: ['measurements', id], queryFn: () => getMeasurement(id) });
export const useGetAllMeasurements = (
  status?: string,
  options?: UseQueryOptions<Measurement[], unknown, Measurement[], [string, string | undefined]>
) =>
  useQuery<Measurement[], unknown, Measurement[], [string, string | undefined]>({
    queryKey: ['measurements', status],
    queryFn: () => getAllMeasurements(status),
    initialData: [],
    ...options,
  });
export const useCreateMeasurement = (
  options?: UseMutationOptions<Measurement, unknown, any>
) => {
  const queryClient = useQueryClient();

  return useMutation(createMeasurement, {
    onSuccess: () => {
      void queryClient.invalidateQueries(['measurements']);
    },
    ...options,
  });
};
export const useUpdateMeasurement = () => {
  const queryClient = useQueryClient();

  return useMutation(updateMeasurement, {
    onSuccess: () => {
      void queryClient.invalidateQueries(['measurements']);
    },
  });
};
export const useCloseMeasurement = () => {
  const queryClient = useQueryClient();

  return useMutation((id: number | string) => closeMeasurement(id), {
    onSuccess: () => {
      void queryClient.invalidateQueries(['measurements']);
      void queryClient.invalidateQueries(['dashboardOverview']);
    },
  });
};
export const useDeleteMeasurement = () => {
  const queryClient = useQueryClient();

  return useMutation((measurement: Measurement) => deleteMeasurement(measurement.id), {
    onSuccess: () => {
      void queryClient.invalidateQueries(['measurements']);
    },
  });
};

// yards
export const useGetYard = (id: number | string) =>
  useQuery({ queryKey: ['yards', id], queryFn: () => getYard(id) });
export const useGetAllYards = (options?: UseQueryOptions<Yard[], unknown, Yard[], [string]>) =>
  useQuery<Yard[], unknown, Yard[], [string]>({
    queryKey: ['yards'],
    queryFn: getAllYards,
    initialData: [],
    ...options,
  });
export const useCreateYard = (options?: UseMutationOptions<Yard, unknown, Yard>) => {
  const queryClient = useQueryClient();

  return useMutation(createYard, {
    onSuccess: (newTag) => {
      void queryClient.invalidateQueries(['yards']);
    },
    ...options,
  });
};
export const useUpdateYard = () => {
  const queryClient = useQueryClient();

  return useMutation(updateYard, {
    onSuccess: () => {
      void queryClient.invalidateQueries(['yards']);
    },
  });
};
export const useDeleteYard = () => {
  const queryClient = useQueryClient();

  return useMutation((yard: Yard) => deleteYard(yard.id), {
    onSuccess: () => {
      void queryClient.invalidateQueries(['yards']);
    },
  });
};

// certifications
export const useGetCertification = (id: number | string) =>
  useQuery({ queryKey: ['certifications', id], queryFn: () => getCertification(id) });
export const useGetAllCertifications = (
  options?: UseQueryOptions<Certification[], unknown, Certification[], [string]>
) =>
  useQuery<Certification[], unknown, Certification[], [string]>({
    queryKey: ['certifications'],
    queryFn: getAllCertifications,
    initialData: [],
    ...options,
  });
export const useCreateCertification = (
  options?: UseMutationOptions<Certification, unknown, Certification>
) => {
  const queryClient = useQueryClient();

  return useMutation(createCertification, {
    onSuccess: (newTag) => {
      void queryClient.invalidateQueries(['certifications']);
    },
    ...options,
  });
};
export const useUpdateCertification = () => {
  const queryClient = useQueryClient();

  return useMutation(updateCertification, {
    onSuccess: () => {
      void queryClient.invalidateQueries(['certifications']);
    },
  });
};
export const useDeleteCertification = () => {
  const queryClient = useQueryClient();

  return useMutation((certification: Certification) => deleteCertification(certification.id), {
    onSuccess: () => {
      void queryClient.invalidateQueries(['certifications']);
    },
  });
};


// users
export const useGetUser = (id: number | string) =>
  useQuery({ queryKey: ['users', id], queryFn: () => getYard(id) });
export const useGetAllUsers = (options?: UseQueryOptions<User[], unknown, User[], [string]>) =>
  useQuery<User[], unknown, User[], [string]>({
    queryKey: ['users'],
    queryFn: getAllUsers,
    initialData: [],
    ...options,
  });
export const useCreateUser = (options?: UseMutationOptions<User, unknown, User>) => {
  const queryClient = useQueryClient();

  return useMutation(createUser, {
    onSuccess: (newTag) => {
      void queryClient.invalidateQueries(['users']);
    },
    ...options,
  });
};
export const useUpdateUser = () => {
  const queryClient = useQueryClient();

  return useMutation(updateUser, {
    onSuccess: () => {
      void queryClient.invalidateQueries(['users']);
    },
  });
};
export const useDeleteUser = () => {
  const queryClient = useQueryClient();

  return useMutation((user: User) => deleteUser(user.id), {
    onSuccess: () => {
      void queryClient.invalidateQueries(['users']);
    },
  });
};

// Profile
export const useUpdateProfile = (onSuccess?: VoidFunction, onError?: (error: ApiError) => void) => {
  const queryClient = useQueryClient();

  return useMutation(updateProfile, {
    onSuccess: () => {
      void queryClient.invalidateQueries(['profile']);
      onSuccess?.();
    },
    onError,
  });
};
export const useChangeProfilePassword = (
  onSuccess?: VoidFunction,
  onError?: (error: ApiError) => void
) =>
  useMutation(changeProfilePassword, {
    onSuccess,
    onError,
  });
export const useGetMe = (options?: UseQueryOptions<Me, unknown, Me, [string]>) =>
  useQuery<Me, unknown, Me, [string]>({
    queryKey: ['profile'],
    queryFn: me,
    initialData: undefined,
    ...options,
  });

// Authentication
export const useResetPassword = (onSuccess?: VoidFunction, onError?: (error: ApiError) => void) =>
  useMutation(resetPassword, {
    onSuccess,
    onError,
  });

// Logs (strumenti)
export const useGetAllLogs = (
  options?: UseQueryOptions<Log[], unknown, Log[], [string]>
) =>
  useQuery<Log[], unknown, Log[], [string]>({
    queryKey: ['logs'],
    queryFn: getAllLogs,
    initialData: [],
    ...options,
  });
export const useCreateLog = () => {
  const queryClient = useQueryClient();
  return useMutation(createLog, {
    onSuccess: () => {
      void queryClient.invalidateQueries(['logs']);
    },
  });
};
export const useUpdateLog = () => {
  const queryClient = useQueryClient();
  return useMutation(updateLog, {
    onSuccess: () => {
      void queryClient.invalidateQueries(['logs']);
    },
  });
};
export const useDeleteLog = () => {
  const queryClient = useQueryClient();
  return useMutation((log: Log) => deleteLog(log.id), {
    onSuccess: () => {
      void queryClient.invalidateQueries(['logs']);
    },
  });
};

// Configuration
export const useGetTemplateInfo = (
  options?: UseQueryOptions<TemplateInfo, unknown, TemplateInfo, [string]>
) =>
  useQuery<TemplateInfo, unknown, TemplateInfo, [string]>({
    queryKey: ['templateInfo'],
    queryFn: getTemplateInfo,
    ...options,
  });
export const useUploadTemplate = () => {
  const queryClient = useQueryClient();
  return useMutation((file: File) => uploadTemplate(file), {
    onSuccess: () => {
      void queryClient.invalidateQueries(['templateInfo']);
    },
  });
};

// Clients
export const useGetAllClients = (
  options?: UseQueryOptions<Client[], unknown, Client[], [string]>
) =>
  useQuery<Client[], unknown, Client[], [string]>({
    queryKey: ['clients'],
    queryFn: getAllClients,
    initialData: [],
    ...options,
  });
export const useCreateClient = () => {
  const queryClient = useQueryClient();
  return useMutation(createClient, {
    onSuccess: () => {
      void queryClient.invalidateQueries(['clients']);
    },
  });
};
export const useUpdateClient = () => {
  const queryClient = useQueryClient();
  return useMutation(updateClient, {
    onSuccess: () => {
      void queryClient.invalidateQueries(['clients']);
    },
  });
};
export const useDeleteClient = () => {
  const queryClient = useQueryClient();
  return useMutation((client: Client) => deleteClient(client.id), {
    onSuccess: () => {
      void queryClient.invalidateQueries(['clients']);
    },
  });
};
